import type { ReactNode } from "react";

const swallow = (event: React.SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

const swallowKey = (event: React.KeyboardEvent): void => {
  if (event.key === "Enter" || event.key === " ") swallow(event);
};

/**
 * `A-1a` (`DX-B-017`): **an act that waits, whose reason is said once ELSEWHERE.**
 *
 * The kit's `GuardedButton` prints its reason beside itself, which is right for one control and wrong for three
 * that wait on the same thing: the LCB-FT card said « Indiquez si le criblage a été effectué… » three times, once
 * beside « Conforme », once beside « Signaler » and once beside Hakili's « Confirmer », and the card spilled out of
 * its width. Here the sentence stands ONCE, at the question, and each inert act points at it with `aria-describedby`.
 *
 * Same refusal as `GuardedButton` (`DX-009`, D-1018-6): `aria-disabled`, never `disabled` (a `disabled` button leaves
 * the tab order, and nobody hears why it is dead), and the click and the two keys that activate a button are
 * cancelled. A request to F: `GuardedButton` taking the id of a reason it does not print.
 */
export function InertAct(props: {
  readonly describedBy: string;
  readonly testId: string;
  readonly children: ReactNode;
  /** Replaces the act's small default look (the composer's send is a large control, not a row's act). */
  readonly className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-disabled="true"
      aria-describedby={props.describedBy}
      onClick={swallow}
      onKeyDown={swallowKey}
      onKeyUp={swallowKey}
      data-testid={props.testId}
      className={
        props.className ??
        "cursor-not-allowed whitespace-nowrap rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] font-medium text-gray-600"
      }
    >
      {props.children}
    </button>
  );
}
